import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import { http, formatApiError, toastApiError } from "../lib/api";
import { recordLink } from "../lib/notify";
import { useNavDraft } from "../lib/navDraft";
import { toast } from "../lib/notify";
import { useAuth } from "../context/AuthContext";
import { RESTRICTED_ROLES, PROJECT_SCOPED_ROLES, isTaseronRole, allowedEntryGroups } from "../lib/roles";
import { parseTL } from "../components/AmountInput";
import { useColFilters } from "../components/ColFilters";
import EntriesTable, { ENTRY_COLS } from "../components/EntriesTable";
import EntriesBulkUpload from "../components/EntriesBulkUpload";
import { Field, ReadonlyBox, EditBanner, inputCls } from "../components/dataentry/fields";
import AreaPicker, { computeAreaQty } from "../components/dataentry/AreaPicker";
import SubcontractorField from "../components/dataentry/SubcontractorField";
import PriceFields from "../components/dataentry/PriceFields";
import EntryModeToggles from "../components/dataentry/EntryModeToggles";
import EntrySummary from "../components/dataentry/EntrySummary";
import ApprovalPanel from "../components/dataentry/ApprovalPanel";
import TimesheetSection from "../components/dataentry/TimesheetSection";
import EntryChain from "../components/dataentry/EntryChain";
import EntryGroupPicker from "../components/dataentry/EntryGroupPicker";
import EkstraEntryForm from "../components/dataentry/EkstraEntryForm";
import PersonelEntryForm from "../components/dataentry/PersonelEntryForm";
import AtmWipesPanel from "../components/atm/AtmWipesPanel";
import CorrectionEntryForm from "../components/dataentry/CorrectionEntryForm";
import { useEntryData } from "../components/dataentry/useEntryData";
import { useApprovalFlow } from "../components/dataentry/useApprovalFlow";
import { emptyForm, initialForm, serviceVisible, categoryVisible, cascade, focusNextOnEnter, sortEntries, findLock, ENTRY_GROUPS, groupOfCategory, initialGroup } from "../components/dataentry/entryHelpers";
import { computePricing, categoryFlags, finiteOrNull, locationOptions, byLabelTr } from "../lib/entryPricing";

const trName = (a, b) => (a.name || "").localeCompare(b.name || "", "tr");

// karttan geri dönüşte (?vurgu=entry:<id>) ilgili kaydı sarı yanıp sönerek işaretle
function useEntryFlash(count) {
  useEffect(() => {
    const v = new URLSearchParams(window.location.search).get("vurgu");
    if (!v || !v.startsWith("entry:")) return;
    const id = v.slice(6);
    const t = setTimeout(() => {
      const row = document.querySelector(`[data-testid="entry-row-${id}"]`);
      if (!row) return;
      row.scrollIntoView({ behavior: "smooth", block: "center" }); row.classList.add("flash-row");
      setTimeout(() => row.classList.remove("flash-row"), 5000);
    }, 900);
    return () => clearTimeout(t);
  }, [count]);
}

// hizmet + lokasyon (+tarih) seçilince aktif atama kartlarını getir; fiyatlar kayıt tarihine göre çözümlenir
function useAssignmentLookup(form, setForm) {
  const [assignOptions, setAssignOptions] = useState(undefined); // undefined: yükleniyor; []: atama yok
  useEffect(() => {
    if (!form.service_id || !form.location_id) { setAssignOptions(undefined); return; }
    let cancelled = false;
    const sid = form.service_id, lid = form.location_id;
    setAssignOptions(undefined);
    http.get("/assignments/lookup", { params: { service_id: sid, location_id: lid, entry_date: form.entry_date } })
      .then((r) => {
        if (cancelled) return;
        const list = r.data?.assignments || [];
        setAssignOptions(list);
        if (list.length === 1 && list[0].subcontractor_id) setForm((f) => (f.location_id === lid ? { ...f, subcontractor_id: list[0].subcontractor_id } : f));
      })
      .catch(() => { if (!cancelled) setAssignOptions([]); });
    return () => { cancelled = true; };
  }, [form.service_id, form.location_id, form.entry_date, setForm]);
  return assignOptions;
}

// düzeltme modu banner'ı → components/dataentry/fields.jsx (EditBanner)

export default function DataEntry() {
  const { user } = useAuth();
  if (isTaseronRole(user?.role)) return <TaseronAtmEntry />;
  return <DataEntryFull />;
}

// Taşeron / taşeron kullanıcısı: yalnız ATM Silim kutusu; backend yalnız kendisine tanımlı ATM'leri döndürür (proje seçimi → liste → Silim Yap)
function TaseronAtmEntry() {
  return (
    <div className="space-y-6" data-testid="page-data-entry" data-taseron="1">
      <div>
        <h1 className="text-3xl font-bold tracking-tight">Hizmet Veri Girişi</h1>
        <p className="text-sm text-slate-500 mt-1"><span className="font-semibold text-slate-700">ATM Silim Hizmeti Veri Girişi</span> — yalnız size tanımlı ATM'ler listelenir. Projeyi seçin, listeden "Silim Yap" ile fotoğraflı silim kaydı girin (Gerçekleşen hemen artar).</p>
      </div>
      <EntryGroupPicker value="atm" onChange={() => {}} only={["atm"]} />
      <div className="bg-white border border-slate-200 rounded-lg p-6" data-testid="entry-atm-wipes"><AtmWipesPanel embedded requireProject /></div>
    </div>
  );
}

function DataEntryFull() {
  const { isAdmin, user } = useAuth();
  const restricted = RESTRICTED_ROLES.includes(user?.role);   // şube / müşteri yetkilisi: taşeron (yüklenici) bilgisi ve atama uyarıları tamamen gizli
  const scopedRole = PROJECT_SCOPED_ROLES.includes(user?.role);   // + proje koordinatörü: yalnız yetkili olduğu proje/şube (backend listeyi kısıtlar); tek seçenek → otomatik ve kilitli
  const navigate = useNavigate();
  const [form, setForm] = useState(initialForm);
  const allowedGroups = useMemo(() => allowedEntryGroups(user), [user]);   // null = tüm giriş türleri; yetkiyle sınırlıysa yalnız izinli kutular
  const [group, setGroup] = useState(() => { const g = initialGroup(); return g && allowedGroups && !allowedGroups.includes(g) ? null : g; });   // veri giriş türü: personel | ekstra | atm (üst menü kutuları)
  const { projects, locations, categories, services, subs, projectSubs, claimLocks, allLocs, setAllLocs, entries, groupCounts: serverCounts, userLabels, appUsers, loadAll } = useEntryData(isAdmin, form.project_id, group);
  const [busy, setBusy] = useState(false);
  const [telafi, setTelafi] = useState(false);
  const [fiyatFarki, setFiyatFarki] = useState(false);
  const [baskaTaseron, setBaskaTaseron] = useState(false);
  const [ffPrice, setFfPrice] = useState("");
  const [editEntry, setEditEntry] = useState(null);
  const [editDay, setEditDay] = useState(null);   // gün düzeltme: {project_id, location_id, entry_date, entries}
  const editMode = !!editEntry;
  const assignOptions = useAssignmentLookup(form, setForm);
  useEntryFlash(entries.length);
  // listeden Düzelt: form üste açılır; Vazgeç / Güncelle sonrası listede kalınan yere geri dönülür
  const returnPos = useRef(null);
  const rememberReturn = () => { returnPos.current = { y: window.scrollY, top: document.querySelector('[data-testid="entries-scroll"]')?.scrollTop ?? 0 }; };
  const restoreReturn = () => {
    const p = returnPos.current;
    if (!p) return;
    returnPos.current = null;
    setTimeout(() => { const el = document.querySelector('[data-testid="entries-scroll"]'); if (el) el.scrollTop = p.top; window.scrollTo({ top: p.y, behavior: "smooth" }); }, 80);
  };
  // taslak: yalnız kullanıcının yazdığı veri sayılır — otomatik dolan miktar (ATM silim / m²) ve yalnız seçim/bağlam "soft" (rozet/uyarı yok)
  const atmAutoQty = useRef(null);
  const areaAutoQty = useRef(null);
  const [areaSel, setAreaSel] = useState([]);
  const [areaManual, setAreaManual] = useState({});
  const userQty = () => { const q = String(form.quantity ?? ""); return q !== "" && q !== String(atmAutoQty.current ?? "") && q !== String(areaAutoQty.current ?? ""); };
  useNavDraft("hizmet-veri-girisi", () => (form.service_id || form.quantity || form.description || editEntry ? { form, telafi, fiyatFarki, baskaTaseron, ffPrice, editEntry, soft: !(userQty() || form.description || editEntry) } : null),
    (d) => { setForm(d.form); setTelafi(!!d.telafi); setFiyatFarki(!!d.fiyatFarki); setBaskaTaseron(!!d.baskaTaseron); setFfPrice(d.ffPrice || ""); if (d.editEntry) setEditEntry(d.editEntry); });

  const cancelEdit = useCallback(() => { setEditEntry(null); setEditDay(null); setTelafi(false); setForm({ ...emptyForm }); restoreReturn(); }, []);   // eslint-disable-line react-hooks/exhaustive-deps
  // sayfa içi bağlantı (?proje=&kategori=&hizmet=&tarih=&git=…): ör. sarkan izin rozetinden kaynak ayın puantajına — form/tür değişir, satır vurgulanır (vurgu=ts:…)
  const { search } = useLocation();
  useEffect(() => {
    const sp = new URLSearchParams(search);
    if (!sp.get("git") || !sp.get("proje")) return;
    setEditEntry(null);
    setForm(initialForm());
    const c = categories.find((x) => x.id === sp.get("kategori"));
    if (c) setGroup(groupOfCategory(c));
  }, [search, categories]);
  // İş Emirleri → "Düzelt" (?duzenle=<id>): kayıt listeden bulunup düzenleme modunda açılır
  useEffect(() => {
    if (!editEntry && !editDay) return;
    const onKey = (ev) => { if (ev.key !== "Escape" || ev.target.closest("[data-searchable]")) return; ev.preventDefault(); ev.stopImmediatePropagation(); cancelEdit(); };   // ESC önce düzeltmeyi kapatır, bölümü kapatmaz
    window.addEventListener("keydown", onKey, true);
    return () => window.removeEventListener("keydown", onKey, true);
  }, [editEntry, editDay, cancelEdit]);

  const lockOf = useCallback((e) => findLock(claimLocks, e), [claimLocks]);

  const groupOfEntry = useMemo(() => {
    const byCat = Object.fromEntries(categories.map((c) => [c.id, groupOfCategory(c)]));
    return (e) => byCat[e.category_id] || groupOfCategory({ name: e.category_name });
  }, [categories]);
  // Düzelt: kayıt üstteki veri giriş formuna yüklenir, form "düzenleme modu"nda açılır
  const openEdit = (e) => {
    rememberReturn();
    setEditDay(null);
    setEditEntry(e);
    setGroup(groupOfCategory(categories.find((c) => c.id === e.category_id) || { name: e.category_name }));
    setAreaSel(e.area_keys || []);
    setAreaManual(Object.fromEntries(Object.entries(e.area_m2_manual || {}).map(([k, v]) => [k, String(v).replace(".", ",")])));
    setTelafi(!!e.is_compensation); setFiyatFarki(false); setFfPrice(""); setBaskaTaseron(false);
    setForm({
      project_id: e.project_id || "", category_id: e.category_id || "", service_id: e.service_id || "", location_id: e.location_id || "",
      subcontractor_id: e.actual_subcontractor_id || e.subcontractor_id || "",
      sell_price: e.customer_unit_price ?? "", buy_price: e.subcontractor_unit_price ?? "",
      entry_date: e.entry_date, quantity: e.quantity ?? "", description: e.description || "",
    });
    setTimeout(() => {
      (document.querySelector('[data-testid="entry-edit-banner"]') || document.querySelector('[data-testid="entry-form"]'))?.scrollIntoView({ behavior: "smooth", block: "start" });
      document.querySelector('[data-testid="entry-date"]')?.focus();
    }, 50);
  };
  // Gün düzeltme (listeden): ekstra kayıtlarda günün tüm hizmetleri veri giriş formunda açılır; tek kayıt / diğer türler tekli düzeltmeye düşer
  const openEditDay = (g) => {
    const items = g.active.filter((e) => !e.personnel_id);
    if (!items.length) return;
    if (items[0].is_correction || groupOfEntry(items[0]) !== "ekstra") return openEdit(items[0]);   // düzeltme farkı: tekil form
    rememberReturn();
    setEditEntry(null); setTelafi(false); setFiyatFarki(false); setFfPrice(""); setBaskaTaseron(false);
    setGroup("ekstra");
    setForm({ ...emptyForm, project_id: g.project_id, location_id: g.location_id, entry_date: g.entry_date });
    setEditDay({ project_id: g.project_id, location_id: g.location_id, entry_date: g.entry_date, entries: items });
    setTimeout(() => document.querySelector('[data-testid="entry-edit-banner"]')?.scrollIntoView({ behavior: "smooth", block: "start" }), 50);
  };

  const duzenleDone = useRef("");
  useEffect(() => {
    const id = new URLSearchParams(search).get("duzenle");
    if (!id || !entries.length || duzenleDone.current === id) return;
    const e = entries.find((x) => x.id === id);
    if (!e) return;
    duzenleDone.current = id;
    openEdit(e);
  }, [search, entries]);   // eslint-disable-line react-hooks/exhaustive-deps
  const { category_id: fCat, project_id: fProj } = form;
  const filteredServices = useMemo(() => services.filter((s) => serviceVisible(s, { category_id: fCat, project_id: fProj })), [services, fCat, fProj]);
  const location = locations.find((l) => l.id === form.location_id);
  const service = services.find((s) => s.id === form.service_id);
  const serviceLocations = useMemo(() => {
    const all = locations.filter((l) => l.project_id === form.project_id);
    const base = group === "atm" && all.some((l) => l.branch_type === "ATM") ? all.filter((l) => l.branch_type === "ATM") : all;   // ATM silim girişinde lokasyon = ATM
    if (service && Array.isArray(service.location_ids) && service.location_ids.length > 0) return base.filter((l) => service.location_ids.includes(l.id));
    return base;
  }, [locations, form.project_id, service, group]);
  const projectOpts = useMemo(() => projects.filter((p) => p.status === "Aktif").sort(trName).map((p) => ({ value: p.id, label: p.name })), [projects]);
  const categoryOpts = useMemo(() => categories.filter((c) => categoryVisible(c, form.project_id) && (!group || groupOfCategory(c) === group)).map((c) => ({ value: c.id, label: c.name })).sort(byLabelTr), [categories, form.project_id, group]);
  const groupCounts = useMemo(() => serverCounts || entries.reduce((acc, e) => { const g = groupOfEntry(e); acc[g] = (acc[g] || 0) + 1; return acc; }, Object.fromEntries(ENTRY_GROUPS.map((g) => [g.key, 0]))), [serverCounts, entries, groupOfEntry]);
  const serviceOpts = useMemo(() => filteredServices.map((s) => ({ value: s.id, label: `${s.name} (${s.unit})` })).sort(byLabelTr), [filteredServices]);
  const locationOpts = useMemo(() => locationOptions(serviceLocations), [serviceLocations]);

  // "Tüm Kayıtlar" listesi seçili giriş türünün kayıtlarını gösterir (personel / ekstra / atm); tür seçilmeden tümü
  const groupEntries = useMemo(() => (group ? entries.filter((e) => groupOfEntry(e) === group) : entries), [entries, group, groupOfEntry]);
  const sortedEntries = useMemo(() => sortEntries(groupEntries), [groupEntries]);
  const [ehf, setEhf, visEntries, eAutoOpts] = useColFilters(sortedEntries, ENTRY_COLS);
  const appr = useApprovalFlow({ visEntries, entries: groupEntries, allLocs, setAllLocs, form, loadAll, navigate });

  // tek seçenekli kademeler otomatik seçilir (örn. WILCO MERKEZ: tek hizmet + tek lokasyon)
  useEffect(() => {
    if (scopedRole && !editMode && !form.project_id && projectOpts.length === 1) setForm((f) => ({ ...f, project_id: projectOpts[0].value }));
  }, [scopedRole, editMode, form.project_id, projectOpts]);
  useEffect(() => {
    if (scopedRole && !editMode && form.project_id && !form.location_id && locations.length === 1) setForm((f) => ({ ...f, location_id: locations[0].id }));
  }, [scopedRole, editMode, form.project_id, form.location_id, locations]);
  useEffect(() => {
    if (form.category_id && !form.service_id && filteredServices.length === 1) setForm((f) => ({ ...f, service_id: filteredServices[0].id }));
  }, [filteredServices, form.category_id, form.service_id]);
  // seçili giriş türünde projede tek ana başlık varsa (ör. ATM, Ekstra Temizlik) otomatik seçilir
  useEffect(() => {
    if (group && form.project_id && !form.category_id && categoryOpts.length === 1) setForm((f) => ({ ...f, category_id: categoryOpts[0].value }));
  }, [group, form.project_id, form.category_id, categoryOpts]);
  // URL / taslak / düzeltme ile gelen ana başlık → giriş türü kutusu otomatik seçilir
  useEffect(() => {
    if (group || !form.category_id) return;
    const c = categories.find((x) => x.id === form.category_id);
    if (c) setGroup(groupOfCategory(c));
  }, [group, form.category_id, categories]);
  const pickGroup = (g) => {
    if (g === group || (allowedGroups && !allowedGroups.includes(g))) return;
    if (editMode || editDay) cancelEdit();
    setGroup(g);
    setForm((f) => ({ ...emptyForm, project_id: f.project_id, entry_date: f.entry_date }));   // proje ve tarih korunur, zincir sıfırlanır
    setTelafi(false); setFiyatFarki(false); setFfPrice(""); setBaskaTaseron(false);
  };
  const groupDef = ENTRY_GROUPS.find((g) => g.key === group);
  // yetkide tek giriş türü varsa kutu otomatik seçilir
  useEffect(() => {
    if (!group && allowedGroups && allowedGroups.length === 1) setGroup(allowedGroups[0]);
  }, [group, allowedGroups]);
  // ATM hizmeti tanımlı aktif projeler (ATM ana başlığı + en az bir aktif ATM hizmeti) → ATM Silim panelinde proje listesi
  const atmServiceProjects = useMemo(() => {
    const atmCats = categories.filter((c) => groupOfCategory(c) === "atm");
    return projects.filter((p) => p.status === "Aktif" && atmCats.some((c) => categoryVisible(c, p.id) && services.some((s) => serviceVisible(s, { category_id: c.id, project_id: p.id })))).map((p) => [p.id, p.name]);
  }, [projects, categories, services]);
  // Ekstra temizlik girişi: projenin ekstra ana başlıklarındaki aktif alt hizmetleri (proje kartına eklenen hizmet burada otomatik görünür)
  const servicesOfGroup = (g) => {
    if (!form.project_id) return [];
    const cats = categories.filter((c) => groupOfCategory(c) === g && categoryVisible(c, form.project_id));
    return services.filter((s) => cats.some((c) => serviceVisible(s, { category_id: c.id, project_id: form.project_id }))).sort(trName);
  };
  const ekstraServices = useMemo(() => servicesOfGroup("ekstra"), [categories, services, form.project_id]);  // eslint-disable-line react-hooks/exhaustive-deps
  const personelServices = useMemo(() => servicesOfGroup("personel"), [categories, services, form.project_id]);  // eslint-disable-line react-hooks/exhaustive-deps
  const ekstraMode = group === "ekstra";
  const personelMode = group === "personel";
  const correctionMode = !!location?.is_correction && !!group;   // DÜZELTME FARKLARI şubesi seçildi → ± tutar formu (grup formunun yerine)
  const correctionServices = useMemo(() => servicesOfGroup(group), [group, categories, services, form.project_id]);  // eslint-disable-line react-hooks/exhaustive-deps
  const onCorrectionSaved = async (mode) => {
    if (mode === "edit") { setEditEntry(null); setEditDay(null); setForm({ ...emptyForm }); restoreReturn(); }
    await loadAll();
  };
  const legacyForm = group === "atm" && editMode;   // eski tekli form yalnız ATM kaydı düzeltmede
  // "Aynısını tekrar gir": kullanıcının (yoksa herkesin) son kaydettiği ekstra hizmet seti — aynı proje + lokasyon + tarih + giren
  const lastEkstraBatch = useMemo(() => {
    const pool = entries.filter((e) => groupOfEntry(e) === "ekstra" && !e.is_price_diff && e.status !== "İptal" && e.service_id);
    const mine = pool.filter((e) => e.created_by === user?.email);
    const src = mine.length ? mine : pool;
    if (!src.length) return null;
    const latest = [...src].sort((a, b) => String(b.created_at || "").localeCompare(String(a.created_at || "")))[0];
    const items = src.filter((e) => e.project_id === latest.project_id && e.location_id === latest.location_id && e.entry_date === latest.entry_date && e.created_by === latest.created_by);
    const seen = new Set();
    return { project_id: latest.project_id, location_id: latest.location_id, entry_date: latest.entry_date, project_name: latest.project_name, location_name: latest.location_name,
      items: items.filter((e) => (seen.has(e.service_id) ? false : seen.add(e.service_id))) };
  }, [entries, groupOfEntry, user?.email]);
  const onEkstraSaved = async (mode) => {
    if (mode === "edit") {
      const sp = new URLSearchParams(search);
      const rid = editEntry?.id;
      setEditEntry(null); setEditDay(null); setTelafi(false); setForm({ ...emptyForm });
      restoreReturn();
      if (sp.get("yeniden") === "1" && rid) {   // Reddedilen talep: Düzelt & Yeniden Gönder → Güncelle sonrası otomatik GM onayına
        try { const { data } = await http.post(`/entries/${rid}/resubmit`); toast.success(`Düzeltildi ve yeniden GM onayına gönderildi${data.mailed ? ` · ${data.mailed} GM yetkilisine e-posta` : ""}`); window.dispatchEvent(new Event("approvals-changed")); }
        catch (e) { toastApiError(e); }
        navigate("/veri-girisi?bolum=hizmet", { replace: true });
      }
    }
    else if (mode === "new") { setForm({ ...emptyForm, project_id: form.project_id, entry_date: form.entry_date, location_id: form.location_id }); setTimeout(() => document.querySelector('[data-testid="ekstra-entry-form"] [data-testid="entry-date"]')?.focus(), 80); }   // Kaydet / Yeni → odak Hizmet Tarihi
    else { setForm({ ...emptyForm }); setTimeout(() => document.querySelector('[data-testid="entry-project"]')?.focus(), 50); }
    await loadAll();
  };
  useEffect(() => {
    if (form.service_id && !form.location_id && serviceLocations.length === 1) setForm((f) => ({ ...f, location_id: serviceLocations[0].id }));
  }, [serviceLocations, form.service_id, form.location_id]);
  // ATM şubesi seçilince Miktar = aylık silim sayısı (kullanıcı değiştirebilir; elle girilen değer ezilmez). Karar updater dışında verilir (StrictMode çift çağrıda ref bozulmasın)
  const qtyRef = useRef(form.quantity); qtyRef.current = form.quantity;
  useEffect(() => {
    if (editMode) return;
    const wipes = location?.branch_type === "ATM" ? location.monthly_wipe_count || 0 : 0;
    const q = String(qtyRef.current ?? "");
    const untouched = q === "" || q === String(atmAutoQty.current ?? "");
    if (wipes > 0 && form.service_id && untouched && q !== String(wipes)) { atmAutoQty.current = wipes; setForm((f) => ({ ...f, quantity: String(wipes) })); }
    else if (!wipes && atmAutoQty.current !== null && q === String(atmAutoQty.current)) { atmAutoQty.current = null; setForm((f) => ({ ...f, quantity: "" })); }
  }, [location, form.service_id, editMode]);
  // Tadilat / genel temizlik: Miktar = şube kartındaki ilgili m² (+ genel temizlikte seçilen ek alanlar); elle girilen değer ezilmez
  const { service_id: fSvc, location_id: fLoc } = form;
  useEffect(() => { if (!editMode) { setAreaSel([]); setAreaManual({}); } }, [fSvc, fLoc, editMode]);
  const area = computeAreaQty(location, service, areaSel, areaManual);
  const areaManualClean = Object.fromEntries(area.extras.filter((e) => e.on && !e.card && e.value > 0).map((e) => [e.key, e.value]));
  const areaForce = useRef(false);   // kutucuk tıklaması / elle m² girişi → Miktar her durumda toplamla güncellenir
  const pickArea = (sel) => { areaForce.current = true; setAreaSel(sel); };
  const pickManual = (k, v) => { areaForce.current = true; setAreaManual((m) => ({ ...m, [k]: v })); };
  useEffect(() => {
    const force = areaForce.current; areaForce.current = false;
    if (editMode && !force) return;
    const total = area.mode ? area.total : 0;
    const totalStr = total > 0 ? String(Math.round(total * 100) / 100).replace(".", ",") : "";   // parseTL noktayı binlik ayracı sayar → ondalık virgülle yazılır
    const q = String(qtyRef.current ?? "");
    const untouched = q === "" || q === String(areaAutoQty.current ?? "");
    if (totalStr && (untouched || force) && q !== totalStr) { areaAutoQty.current = totalStr; setForm((f) => ({ ...f, quantity: totalStr })); }
    else if (!totalStr && areaAutoQty.current !== null && q === String(areaAutoQty.current)) { areaAutoQty.current = null; setForm((f) => ({ ...f, quantity: "" })); }
  }, [area.mode, area.total, editMode]);

  const pricing = computePricing({ form, editMode, assignOptions, baskaTaseron, telafi, fiyatFarki, ffPrice, hasService: !!service });
  const { qty, validQty, manualSell, manualBuy, multiMode, assignment, hasCardPrices, baskaOverride, buyPrice, ffNewPrice } = pricing;
  const activeLock = findLock(claimLocks, form);
  const assignmentMissing = !!(form.service_id && form.location_id && assignOptions !== undefined && assignOptions.length === 0);
  const needsManualPrices = !editMode && !!(form.service_id && form.location_id) && !multiMode && !hasCardPrices;
  const selCategory = categories.find((c) => c.id === form.category_id);
  const { isPersonnelCategory, isTempCategory } = categoryFlags(selCategory);
  const timesheetMode = (isPersonnelCategory || isTempCategory) && !!form.service_id;

  const change = (k, v) => setForm((f) => cascade(f, k, v, services));

  const saveEdit = async () => {
    const q = parseTL(form.quantity);
    if (!Number.isFinite(q) || q <= 0) return toast.error("Miktar sıfırdan büyük olmalıdır");
    setBusy(true);
    try {
      await http.put(`/entries/${editEntry.id}`, {
        project_id: form.project_id, location_id: form.location_id, category_id: form.category_id, service_id: form.service_id,
        subcontractor_id: form.subcontractor_id || "", is_compensation: telafi,
        entry_date: form.entry_date, quantity: q,
        sell_price: parseTL(form.sell_price), buy_price: parseTL(form.buy_price),
        description: form.description,
        actual_subcontractor_id: form.subcontractor_id || null,
        area_keys: area.mode === "genel" ? areaSel : [],
        area_m2_manual: area.mode === "genel" ? areaManualClean : {},
      });
      toast.success("Kayıt güncellendi", { link: recordLink(editEntry.id) });
      setEditEntry(null); setTelafi(false); setForm({ ...emptyForm });
      await loadAll();
    } catch (e) { toastApiError(e); } finally { setBusy(false); }
  };

  const validateSubmit = () => {
    if (!validQty) return "Miktar sıfırdan büyük olmalıdır";
    if (multiMode && !form.subcontractor_id) return "Bu şubeye bu hizmet için birden fazla taşeron ataması yapılmıştır — hizmeti sağlayan taşeronu seçiniz";
    if (baskaOverride && (!Number.isFinite(manualBuy) || manualBuy <= 0)) return "Başka taşeron girişinde işi yapan taşeronun birim fiyatını manuel girin";
    if (needsManualPrices && (!Number.isFinite(manualSell) || !Number.isFinite(manualBuy))) return "Birim fiyatlar atama kartında tanımlı değil — müşteri ve taşeron birim fiyatlarını manuel girin";
    if (activeLock) return "Bu dönem için hakediş oluşturulmuş — veri girişi kapalı";
    if (fiyatFarki) {
      if (!Number.isFinite(ffNewPrice) || ffNewPrice <= 0) return "Fiyat farkı için yeni taşeron birim fiyatını girin";
      if (ffNewPrice <= buyPrice) return "Fiyat farkı yalnızca artış yönünde girilebilir — yeni birim fiyat, taşeron birim fiyatından büyük olmalıdır";
    }
    return "";
  };

  const successMessage = () => {
    if (telafi) return "Telafi temizliği kaydedildi — gelir/gidere dahil edilmez.";
    if (fiyatFarki) return "Kayıt ve fiyat farkı satırı oluşturuldu — fark taşeron hakedişine eklenecek.";
    return "Kayıt oluşturuldu. Fiyatlar satıra kilitlendi.";
  };

  const submit = async (e, mode = "new") => {
    e.preventDefault();
    if (editMode) return saveEdit();
    const err = validateSubmit();
    if (err) return toast.error(err);
    setBusy(true);
    try {
      const { data: created } = await http.post("/entries", {
        ...form,
        subcontractor_id: baskaOverride ? assignment.subcontractor_id : form.subcontractor_id,
        actual_subcontractor_id: baskaOverride ? form.subcontractor_id : null,
        quantity: qty,
        is_compensation: telafi,
        area_keys: area.mode === "genel" ? areaSel : [],
        sell_price: hasCardPrices ? null : finiteOrNull(manualSell),
        buy_price: hasCardPrices && !baskaOverride ? null : finiteOrNull(manualBuy),
        price_diff_unit_price: fiyatFarki ? ffNewPrice : null,
      });
      toast.success(successMessage(), created?.id ? { link: recordLink(created.id) } : undefined);
      if (mode === "new") {
        setForm({ ...emptyForm, project_id: form.project_id, entry_date: form.entry_date });
      } else {
        setForm({ ...emptyForm });
        setTimeout(() => document.querySelector('[data-testid="entry-project"]')?.focus(), 50);
      }
      setTelafi(false); setFiyatFarki(false); setFfPrice("");
      await loadAll();
    } catch (err2) { toast.error(formatApiError(err2)); } finally { setBusy(false); }
  };

  const setStatus = async (id, status, msg) => {
    try { await http.patch(`/entries/${id}/status`, { status }); toast.success(msg); await loadAll(); } catch (e) { toastApiError(e); }
  };
  const cancel = (id) => setStatus(id, "İptal", "Kayıt iptal edildi");
  const reactivate = (id) => setStatus(id, "Aktif", "Kayıt aktifleştirildi");
  // gün paneli: günün tüm aktif kayıtlarını iptal et / iptalleri aktifleştir
  const setStatusMany = async (ids, status, msg) => {
    try { await Promise.all(ids.map((id) => http.patch(`/entries/${id}/status`, { status }))); toast.success(msg); } catch (e) { toastApiError(e); }
    await loadAll();
  };
  const cancelMany = (ids) => { if (ids.length && window.confirm(`Günün ${ids.length} kaydı iptal edilsin mi?`)) return setStatusMany(ids, "İptal", `${ids.length} kayıt iptal edildi`); };
  const reactivateMany = (ids) => setStatusMany(ids, "Aktif", `${ids.length} kayıt aktifleştirildi`);
  const remove = async (id) => {
    const entry = entries.find((x) => x.id === id);
    const linked = entry?.is_price_diff || entries.some((x) => x.parent_entry_id === id);
    if (!window.confirm(linked ? "Bu kayıt, bağlı olduğu fiyat farkı / hizmet kaydıyla birlikte kalıcı olarak silinsin mi?" : "Kayıt kalıcı olarak silinsin mi?")) return;
    try { await http.delete(`/entries/${id}`); toast.success("Kayıt silindi"); await loadAll(); } catch (e) { toastApiError(e); }
  };
  // yalnız yönetici: seçili kayıtları toplu sil (hakediş kilitli / fark satırları atlanır ve raporlanır)
  const bulkRemove = async (ids) => {
    if (!isAdmin || !ids.length) return;
    const linked = entries.filter((x) => x.parent_entry_id && ids.includes(x.parent_entry_id)).length;
    if (!window.confirm(`${ids.length} kayıt kalıcı olarak silinsin mi?${linked ? ` (bağlı ${linked} fiyat farkı satırı da silinir)` : ""}\nHakedişi oluşturulmuş kayıtlar atlanır.`)) return;
    try {
      const { data } = await http.post("/entries/bulk-delete", { ids });
      toast.success(`${data.deleted} kayıt silindi`);
      if (data.skipped?.length) toast.warning(`${data.skipped.length} kayıt silinemedi: ${data.skipped.slice(0, 3).map((s) => `${s.label} — ${s.reason}`).join(" · ")}${data.skipped.length > 3 ? " …" : ""}`, { duration: 12000 });
      appr.setApprSel([]);
      await loadAll();
    } catch (e) { toastApiError(e); }
  };
  const onTimesheetSaved = () => { loadAll(); if (editMode) cancelEdit(); };

  return (
    <div className="space-y-8" data-testid="page-data-entry">
      <div>
        <h1 className="text-3xl font-bold tracking-tight">Hizmet Veri Girişi</h1>
        <p className="text-sm text-slate-500 mt-1">
          {group === "atm" ? <><span className="font-semibold text-slate-700">ATM Silim Hizmeti Veri Girişi</span> — önce ATM hizmeti tanımlı projelerden birini seçin; ATM listesi açılır, "Silim Yap" ile fotoğraflı silim kaydı girilir (Gerçekleşen hemen artar, onaya girmez).</>
            : groupDef ? <><span className="font-semibold text-slate-700">{groupDef.title}</span> — {group === "ekstra" ? "Proje → Tarih + Lokasyon seçin; projenin ekstra temizlik alt hizmetleri listelenir, birden fazla hizmeti aynı anda seçip (yeşil) Miktar girerek tek seferde kaydedin. Taşeron ve fiyatlar atama kartından otomatik gelir." : group === "personel" ? "Proje → Hizmet Tarihi seçin; projenin personel hizmetleri (asıl puantaj, geçici görevlendirme, FM saat) satır satır listelenir — hizmete tıklayınca altında personel listesi açılır." : "Proje → Ana Başlık → Hizmet-Gider → Lokasyon seçin. Taşeron ve fiyatlar varsa şube-hizmet atama kartından otomatik gelir; atama yoksa birim fiyatları manuel girebilirsiniz."}</>
            : "Veri giriş türünü seçin: Personel Hizmeti, Ekstra Temizlik Hizmetleri veya ATM Silim Hizmeti."}
        </p>
      </div>

      <EntryGroupPicker value={group} onChange={pickGroup} counts={groupCounts} only={allowedGroups || undefined} />

      {group === "atm" && <div className="bg-white border border-slate-200 rounded-lg p-6" data-testid="entry-atm-wipes"><AtmWipesPanel embedded requireProject projectId={form.project_id} onProjectChange={(v) => change("project_id", v)} extraProjects={atmServiceProjects} /></div>}

      {correctionMode && <CorrectionEntryForm form={form} change={change} location={location} projectName={projects.find((p) => p.id === form.project_id)?.name || ""} services={correctionServices} subs={projectSubs} hideSub={restricted}
        editEntry={editEntry} onCancelEdit={cancelEdit} onSaved={onCorrectionSaved} onChangeLocation={() => change("location_id", "")} />}

      {!correctionMode && ekstraMode && <EkstraEntryForm form={form} change={change} projectOpts={projectOpts} lockProject={scopedRole && projectOpts.length <= 1} lockLocation={scopedRole && locations.length <= 1}
        locations={locations} services={ekstraServices} subs={projectSubs} claimLocks={claimLocks} hideSub={restricted} onSaved={onEkstraSaved} editEntry={editEntry} editDay={editDay} onCancelEdit={cancelEdit} lastBatch={lastEkstraBatch} entries={entries} onEditEntry={openEdit} />}

      {!correctionMode && personelMode && <PersonelEntryForm form={form} change={change} projectOpts={projectOpts} lockProject={scopedRole && projectOpts.length <= 1} services={personelServices} categories={categories}
        locations={locations} claimLocks={claimLocks} entries={entries} onSaved={onTimesheetSaved} editEntry={editEntry} onCancelEdit={cancelEdit} />}

      {!correctionMode && legacyForm && <form onSubmit={submit} onKeyDown={focusNextOnEnter} className={`bg-white border rounded-lg p-6 space-y-5 ${editMode ? "border-sky-400 ring-2 ring-sky-200" : "border-slate-200"}`} data-testid="entry-form" data-group={group}>
        {editMode && <EditBanner entry={editEntry} onCancel={cancelEdit} />}
        <EntryChain form={form} change={change} projectOpts={projectOpts} categoryOpts={categoryOpts} serviceOpts={serviceOpts} locationOpts={locationOpts} timesheetMode={timesheetMode} activeLock={activeLock}
          lockProject={scopedRole && projectOpts.length <= 1} lockLocation={scopedRole && locations.length <= 1}>
          {timesheetMode && <TimesheetSection isTempCategory={isTempCategory} selCategory={selCategory} service={service} form={form} activeLock={activeLock} locations={locations} onSaved={onTimesheetSaved} editPersonnelId={editMode ? editEntry.personnel_id : null} refreshKey={entries} onExit={() => (editMode ? cancelEdit() : change("service_id", ""))} />}
          {!timesheetMode && !restricted && <SubcontractorField form={form} change={change} editMode={editMode} editEntry={editEntry} baskaTaseron={baskaTaseron} setBaskaTaseron={setBaskaTaseron}
            multiMode={multiMode} assignment={assignment} assignOptions={assignOptions} subs={projectSubs} onNewCari={() => navigate("/cariler?yeni=1")} />}
        </EntryChain>

        {!timesheetMode && <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          <ReadonlyBox label="İl / İlçe" value={location ? `${location.city} / ${location.district}` : "—"} />
          <ReadonlyBox label="Ölçü Birimi" value={service?.unit || "—"} />
          {location?.branch_type === "ATM"
            ? <ReadonlyBox label="ATM Konum · Aylık Silim" value={`${location.latitude != null && location.longitude != null ? `${location.latitude} / ${location.longitude}` : "konum —"} · ${location.monthly_wipe_count ? `${new Intl.NumberFormat("tr-TR").format(location.monthly_wipe_count)} silim/ay` : "silim —"}`} testid="entry-atm-info" />
            : <ReadonlyBox label="Şube m²" value={location?.branch_m2 ? `${new Intl.NumberFormat("tr-TR").format(location.branch_m2)} m²` : "—"} testid="entry-branch-m2" />}
        </div>}
        {!timesheetMode && area.mode && <AreaPicker area={area} selected={areaSel} onChange={pickArea} manual={areaManual} onManual={pickManual} disabled={!!activeLock} />}

        {!timesheetMode && <>
          <PriceFields form={form} change={change} editMode={editMode} service={service} baskaOverride={baskaOverride} hasCardPrices={hasCardPrices} assignment={assignment} hideSub={restricted} />
          {!restricted && <EntryModeToggles telafi={telafi} setTelafi={setTelafi} fiyatFarki={fiyatFarki} setFiyatFarki={setFiyatFarki} ffPrice={ffPrice} setFfPrice={setFfPrice} editMode={editMode} service={service} ffNewTotal={pricing.ffNewTotal} ffDiff={pricing.ffDiff} />}
          <Field label="Açıklama (opsiyonel)">
            <textarea rows={2} value={form.description} onChange={(e) => change("description", e.target.value)} data-testid="entry-description" className={inputCls} />
          </Field>
          <EntrySummary needsManualPrices={needsManualPrices} assignmentMissing={assignmentMissing} pricing={pricing} service={service} fiyatFarki={fiyatFarki}
            editMode={editMode} busy={busy} activeLock={activeLock} cancelEdit={cancelEdit} saveEdit={saveEdit} submit={submit} hideSub={restricted} />
        </>}
      </form>}

      {!restricted && <EntriesBulkUpload projects={projects} services={services} subs={subs} onSaved={loadAll} />}

      <ApprovalPanel apprFilter={appr.apprFilter} setApprFilter={appr.setApprFilter} apprSel={appr.apprSel} setApprSel={appr.setApprSel} visEntriesAppr={appr.visEntriesAppr} sendApproval={appr.sendApproval}
        isAdmin={isAdmin} approverMissing={appr.approverMissing} appUsers={appUsers} quickApprover={appr.quickApprover} goApprover={appr.goApprover} showBulk={appr.showBulkApprover} setShowBulk={appr.setShowBulkApprover} loadAll={loadAll} />
      <EntriesTable apprSel={appr.apprSel} setApprSel={appr.setApprSel} onBulkDelete={bulkRemove} visEntries={appr.visEntriesAppr} entries={groupEntries} ehf={ehf} setEhf={setEhf} eAutoOpts={eAutoOpts} isAdmin={isAdmin} lockOf={lockOf} openEdit={openEdit} onEditDay={openEditDay} reactivate={reactivate} remove={remove} cancel={cancel} cancelMany={cancelMany} reactivateMany={reactivateMany} userLabels={userLabels} hideSub={restricted}
        title={groupDef ? `Tüm Kayıtlar — ${groupDef.title.replace(" Veri Girişi", "")}` : "Tüm Kayıtlar"} subtitle={groupDef ? `Yalnız ${groupDef.title.replace(" Veri Girişi", "").toLocaleLowerCase("tr")} kayıtları (aktif + iptal)` : "Tüm giriş türleri — aktif + iptal edilen kayıtlar"} />
    </div>
  );
}
